// ============================================================================
// FarmerSignInPage — combined farmer signup / login / forgot password (#10).
// ============================================================================
// Routes to #farmer-signin. Toggles between three modes:
//   - login  : email + password → farmer JWT
//   - signup : email + password + name → unverified account + verify email
//   - forgot : email → reset link (anti-enumeration generic response)
//
// On successful login: redirect to #my (placeholder until task #11 ships the
// real My Registrations panel). Anti-enumeration baked in throughout.
// ============================================================================

import { useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Mail, UserPlus, LogIn, KeyRound } from 'lucide-react';
import { api } from '../api';
import { useFarmerAuth } from '../farmerAuth';

type Mode = 'login' | 'signup' | 'forgot' | 'signup-sent' | 'forgot-sent';

export function FarmerSignInPage() {
  const { login } = useFarmerAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function reset(toMode: Mode) {
    setMode(toMode);
    setError('');
    setPassword('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'login') {
        const r = await api.farmerLogin({ email: email.trim(), password });
        login(r.token, r.farmer);
        window.location.hash = '#my';
        return;
      }
      if (mode === 'signup') {
        if (password.length < 8) {
          setError('Password must be at least 8 characters.');
          setBusy(false);
          return;
        }
        await api.farmerSignup({
          email: email.trim(),
          password,
          full_name: fullName.trim(),
          phone: phone.trim() || null,
        });
        setMode('signup-sent');
      } else if (mode === 'forgot') {
        await api.farmerRequestPasswordReset(email.trim());
        setMode('forgot-sent');
      }
    } catch (err: any) {
      setError(err?.message || 'Could not complete that request.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
        </div>
      </header>

      <main className="flex-1 max-w-md mx-auto px-4 py-12 w-full">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {/* Header for each mode */}
          <div className="text-center mb-4">
            {mode === 'login' && <LogIn className="h-10 w-10 text-uif-green-dark mx-auto mb-2" />}
            {mode === 'signup' && <UserPlus className="h-10 w-10 text-uif-green-dark mx-auto mb-2" />}
            {mode === 'forgot' && <KeyRound className="h-10 w-10 text-uif-green-dark mx-auto mb-2" />}
            {(mode === 'signup-sent' || mode === 'forgot-sent') && (
              <CheckCircle2 className="h-12 w-12 text-uif-green mx-auto mb-2" />
            )}
            <h1 className="text-xl font-bold text-uif-green-dark">
              {mode === 'login' && 'Sign in'}
              {mode === 'signup' && 'Create an account'}
              {mode === 'forgot' && 'Reset your password'}
              {mode === 'signup-sent' && 'Check your email'}
              {mode === 'forgot-sent' && 'Check your email'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {mode === 'login' && 'Up in Farms producer account'}
              {mode === 'signup' && 'Track your workshop registrations and attendance'}
              {mode === 'forgot' && "We'll email you a link to set a new password"}
              {mode === 'signup-sent' && 'Verify your email, then come back to sign in'}
              {mode === 'forgot-sent' && 'Click the link in the email to set a new password'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 mb-3 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {(mode === 'signup-sent' || mode === 'forgot-sent') && (
            <div className="text-sm text-gray-700 space-y-3">
              <p>
                {mode === 'signup-sent'
                  ? <>We sent a verification link to <strong>{email}</strong>. Click it, then come back here to sign in.</>
                  : <>If a verified account uses <strong>{email}</strong>, a reset link is on the way.</>}
              </p>
              <button
                type="button"
                onClick={() => reset('login')}
                className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Back to sign in
              </button>
            </div>
          )}

          {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
            <form onSubmit={submit} className="space-y-3">
              {mode === 'signup' && (
                <>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Full name</span>
                    <input
                      type="text"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={busy}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={busy}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    />
                  </label>
                </>
              )}
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Email</span>
                <input
                  type="email"
                  required
                  autoFocus={mode !== 'signup'}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                />
              </label>
              {(mode === 'login' || mode === 'signup') && (
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Password {mode === 'signup' && <span className="text-xs text-gray-500 font-normal">(at least 8 characters)</span>}
                  </span>
                  <input
                    type="password"
                    required
                    minLength={mode === 'signup' ? 8 : undefined}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                  />
                </label>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {mode === 'login' && 'Sign in'}
                {mode === 'signup' && 'Create account'}
                {mode === 'forgot' && 'Send reset link'}
              </button>

              <div className="text-xs text-gray-600 text-center space-y-1 pt-1">
                {mode === 'login' && (
                  <>
                    <div>
                      No account yet?{' '}
                      <button type="button" onClick={() => reset('signup')} className="text-uif-green-dark hover:underline font-medium">
                        Create one
                      </button>
                    </div>
                    <div>
                      <button type="button" onClick={() => reset('forgot')} className="text-uif-green-dark hover:underline">
                        Forgot password?
                      </button>
                    </div>
                  </>
                )}
                {mode === 'signup' && (
                  <div>
                    Already have an account?{' '}
                    <button type="button" onClick={() => reset('login')} className="text-uif-green-dark hover:underline font-medium">
                      Sign in
                    </button>
                  </div>
                )}
                {mode === 'forgot' && (
                  <div>
                    <button type="button" onClick={() => reset('login')} className="text-uif-green-dark hover:underline">
                      Back to sign in
                    </button>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="max-w-2xl mx-auto px-4 text-center text-xs text-gray-500">
          Up in Farms LLC · <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">support@upinfarms.com</a>
        </div>
      </footer>
    </div>
  );
}
